import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  EmptyState,
  FormSection,
  IconTracking,
  OptionRow,
  deliveryNote,
  formatMoney,
  pickText,
  useToast,
  zoneForRegion,
  zoneLine,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useRegion } from '../app/region.js';
import { useSettings } from '../app/settings.js';
import { api, ApiError } from '../lib/api.js';
import { canShareContact, requestTelegramContact } from '../lib/telegram.js';

type Delivery = 'COURIER' | 'PICKUP';
type Split = 'SPLIT' | 'TOGETHER';

/**
 * Rasmiylashtirish.
 *
 * Ikki qoida hujjatdan (docs/02-MAHSULOT.md):
 *   1. Bo'lish savoli FAQAT ARALASH buyurtmada ko'rsatiladi.
 *      Standart — "alohida": bir narsani darhol olgan mijoz qolganini
 *      kutishga ko'proq rozi bo'ladi
 *   2. Yetkazish NARXI tizimda yo'q va taxmin ham yozilmaydi —
 *      operator qo'ng'iroq qilib kelishadi
 */
export function CheckoutScreen(): ReactNode {
  const { t, locale } = useSettings();
  const { push } = useNavigation();
  const { regionId, setRegionId } = useRegion();
  const zones = useQuery({ queryKey: ['delivery-zones'], queryFn: api.deliveryZones });
  const queryClient = useQueryClient();

  const cart = useQuery({ queryKey: ['cart'], queryFn: api.cart, retry: false });
  const regions = useQuery({ queryKey: ['regions'], queryFn: api.regions, staleTime: 600_000 });

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+998');
  const [sharingPhone, setSharingPhone] = useState(false);
  const toast = useToast();
  const [address, setAddress] = useState('');
  const [delivery, setDelivery] = useState<Delivery>('COURIER');
  const [split, setSplit] = useState<Split>('SPLIT');
  const [error, setError] = useState<string | null>(null);

  /**
   * Idempotency kaliti — SAVAT HOLATIGA bog'liq, har renderda emas.
   * Shu sababli "Buyurtma berish" ikki marta bosilsa bitta buyurtma
   * yaratiladi (avtopart-api-module skill).
   */
  const idempotencyKey = useMemo(
    () =>
      `${cart.data?.id ?? 'cart'}-${String(cart.data?.itemCount ?? 0)}-${cart.data?.itemsTotal ?? '0'}`,
    [cart.data?.id, cart.data?.itemCount, cart.data?.itemsTotal],
  );

  const createOrder = useMutation({
    mutationFn: () =>
      api.createOrder(
        {
          customerName: name.trim(),
          customerPhone: phone.replace(/\s/g, ''),
          deliveryMethod: delivery,
          splitPreference: split,
          paymentMethod: 'CASH',
          ...(delivery === 'COURIER' && regionId !== null
            ? { regionId, addressLine: address.trim() }
            : {}),
        },
        idempotencyKey,
      ),
    onSuccess: (order) => {
      /*
       * Bu yerda toast YO'Q va bu ataylab. Demoda buyurtma
       * yaratilganda toast chiqadi, lekin bizda undan KUCHLIROQ
       * javob bor — alohida tasdiq ekrani. Ikkalasi birga bo'lsa
       * xabar ekran almashuvi ostida ko'rinmay o'tib ketardi.
       */
      void queryClient.invalidateQueries({ queryKey: ['cart'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      /*
       * Status ekraniga EMAS, tasdiq ekraniga. Mijoz keyingi qadamni
       * (karta raqami telefonda aytiladi, chek botga tashlanadi)
       * aynan o'sha yerda o'qiydi — status ekranida bu tushuntirish
       * yo'q va u "to'lov tugmasi qani?" degan savol bilan qolardi.
       */
      push({ name: 'success', orderId: order.id, number: order.number });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message_(locale) : t('common.error'));
    },
  });

  const items = cart.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="aps-screen">
        <EmptyState title={t('cart.empty')} hint={t('cart.emptyHint')} />
      </div>
    );
  }

  /*
   * Tanlangan hudud uchun zona. Hech bir zonaga kirmagan hudud
   * ZAXIRA zonaga tushadi — "yetkazish yo'q" deyilmaydi.
   */
  const zone = zoneForRegion(zones.data ?? [], regionId);

  const courierReady = delivery === 'PICKUP' || (regionId !== null && address.trim().length > 0);
  const canSubmit =
    name.trim().length > 0 && /^\+998\d{9}$/.test(phone.replace(/\s/g, '')) && courierReady;

  return (
    <div className="aps-screen">
      <h1 className="aps-screen__title">{t('checkout.title')}</h1>

      <FormSection title={t('checkout.contact')}>
        <input
          className="aps-search__input"
          placeholder={t('checkout.name')}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <input
          className="aps-search__input"
          type="tel"
          placeholder="+998 90 123 45 67"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
        />

        {/*
          Telegram raqamini SO'RAB olish — mijoz qo'lda yozmasligi
          uchun. Telegram o'zining ruxsat oynasini ko'rsatadi.

          Tugma FAQAT qo'llab-quvvatlanadigan mijozda chiziladi
          (Bot API 8.0+). Qo'lda yozish yo'li HAR DOIM ochiq
          qoladi — bu qulaylik, yagona yo'l emas.
        */}
        {canShareContact() && (
          <Button
            variant="secondary"
            disabled={sharingPhone}
            onClick={() => {
              setSharingPhone(true);
              void requestTelegramContact()
                .then(async (contactData) => {
                  if (contactData === null) {
                    toast.show(t('checkout.phoneDenied'));
                    return;
                  }
                  const saved = await api.saveTelegramContact(contactData);
                  setPhone(saved.phone);
                  toast.show(t('checkout.phoneShared'), 'success');
                })
                .catch((caught: unknown) => {
                  toast.show(
                    caught instanceof ApiError ? caught.message_(locale) : t('toast.failed'),
                    'danger',
                  );
                })
                .finally(() => {
                  setSharingPhone(false);
                });
            }}
          >
            {t('checkout.useTelegramPhone')}
          </Button>
        )}
      </FormSection>

      <FormSection title={t('checkout.delivery')} hint={deliveryNote(locale)}>
        <OptionRow
          label={t('checkout.courier')}
          selected={delivery === 'COURIER'}
          onSelect={() => {
            setDelivery('COURIER');
          }}
        />
        <OptionRow
          label={t('checkout.pickup')}
          selected={delivery === 'PICKUP'}
          onSelect={() => {
            setDelivery('PICKUP');
          }}
        />

        {delivery === 'COURIER' ? (
          <>
            <select
              className="aps-search__input"
              value={regionId ?? ''}
              aria-label={t('checkout.region')}
              onChange={(event) => {
                setRegionId(event.target.value === '' ? null : event.target.value);
              }}
            >
              <option value="">{t('checkout.region')}</option>
              {(regions.data ?? []).map((region) => (
                <option key={region.id} value={region.id}>
                  {pickText(region.nameUz, region.nameRu, locale)}
                </option>
              ))}
            </select>
            <input
              className="aps-search__input"
              placeholder={t('checkout.address')}
              value={address}
              onChange={(event) => {
                setAddress(event.target.value);
              }}
            />

            {/*
              Hudud tanlangach YETKAZISH VARIANTI ko'rsatiladi.
              Demodagi ssenariy: "Samarqandni tanlang → variant
              o'zgaradi".

              NARX yo'q va bu ataylab: u har lokatsiya uchun taksi
              bilan o'sha zahoti gaplashiladi (7-javob). Mijoz
              KIM olib borishini va QANCHA VAQT ketishini biladi —
              summani operator telefonda aytadi.
            */}
            {zone !== null && (
              <p className="aps-zone" data-fallback={zone.isFallback ? '' : undefined}>
                <IconTracking size={16} />
                <span>{zoneLine(zone, locale)}</span>
              </p>
            )}
          </>
        ) : null}
      </FormSection>

      {/* Bo'lish savoli FAQAT aralash buyurtmada. Bir xil turdagi
          tovarlarda u ma'nosiz va ortiqcha qadam bo'lardi. */}
      {cart.data?.hasMixedSupply === true ? (
        <FormSection
          title={t('checkout.splitTitle')}
          hint={t('checkout.splitHint').replace('{days}', String(cart.data.maxLeadTimeDays))}
        >
          <OptionRow
            label={t('checkout.splitSeparate')}
            hint={t('checkout.splitSeparateHint')}
            selected={split === 'SPLIT'}
            onSelect={() => {
              setSplit('SPLIT');
            }}
          />
          <OptionRow
            label={t('checkout.splitTogether')}
            hint={t('checkout.splitTogetherHint')}
            selected={split === 'TOGETHER'}
            onSelect={() => {
              setSplit('TOGETHER');
            }}
          />
        </FormSection>
      ) : null}

      <div className="aps-screen__header">
        <span className="aps-section-title">{t('cart.total')}</span>
        <span className="aps-price">
          <span className="aps-price__value">
            {formatMoney(cart.data?.itemsTotal ?? '0', locale)}
          </span>
        </span>
      </div>

      {error === null ? null : <p className="aps-stock aps-stock--out">{error}</p>}

      <Button
        block
        disabled={!canSubmit || createOrder.isPending}
        onClick={() => {
          setError(null);
          createOrder.mutate();
        }}
      >
        {t('checkout.submit')}
      </Button>
    </div>
  );
}
